import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Clock, 
  Calendar, 
  AlertTriangle,
  Info,
  Infinity
} from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

export interface DurationOption {
  value: string;
  label: string;
  description: string;
  recommended?: boolean;
  emergency?: boolean;
}

interface DurationSelectorProps {
  selectedDuration: string;
  onDurationChange: (duration: string) => void;
  defaultDuration?: string;
  patientSettings?: {
    auto_approve_duration?: string;
    allow_emergency_access?: boolean;
  };
  isEmergency?: boolean;
  onEmergencyChange?: (emergency: boolean) => void;
  className?: string;
}

const DurationSelector: React.FC<DurationSelectorProps> = ({
  selectedDuration,
  onDurationChange,
  defaultDuration = '30 days',
  patientSettings,
  isEmergency = false,
  onEmergencyChange,
  className
}) => {
  const [customDuration, setCustomDuration] = useState('');
  const [customUnit, setCustomUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('days');
  const [showCustom, setShowCustom] = useState(false);

  const predefinedDurations: DurationOption[] = [
    {
      value: '2 hours',
      label: '2 Horas',
      description: 'Acesso temporário para consulta rápida',
      emergency: true
    },
    {
      value: '24 hours',
      label: '24 Horas',
      description: 'Acesso de emergência ou consulta urgente',
      emergency: true
    },
    {
      value: '3 days',
      label: '3 Dias',
      description: 'Período curto para acompanhamento'
    },
    {
      value: '7 days',
      label: '1 Semana',
      description: 'Acompanhamento semanal'
    },
    {
      value: '15 days',
      label: '15 Dias',
      description: 'Período quinzenal'
    },
    {
      value: '30 days',
      label: '30 Dias',
      description: 'Período padrão mensal',
      recommended: true
    },
    {
      value: '60 days',
      label: '60 Dias',
      description: 'Período bimestral'
    },
    {
      value: '90 days',
      label: '90 Dias',
      description: 'Período trimestral'
    },
    {
      value: '180 days',
      label: '6 Meses',
      description: 'Período semestral'
    },
    {
      value: '365 days',
      label: '1 Ano',
      description: 'Período anual para acompanhamento contínuo'
    },
    {
      value: 'never',
      label: 'Permanente',
      description: 'Acesso permanente sem data de expiração'
    }
  ];

  // Filtrar durações baseado no modo de emergência
  const availableDurations = isEmergency 
    ? predefinedDurations.filter(d => d.emergency || d.value === defaultDuration)
    : predefinedDurations;

  // Verificar se a duração selecionada é personalizada
  useEffect(() => {
    const isPredefined = predefinedDurations.some(d => d.value === selectedDuration);
    if (!isPredefined && selectedDuration) {
      setShowCustom(true);
      // Tentar extrair valor e unidade da duração personalizada
      const match = selectedDuration.match(/^(\d+)\s+(hours?|days?|weeks?|months?)$/);
      if (match) {
        setCustomDuration(match[1]);
        setCustomUnit(match[2].replace(/s$/, '') as any);
      }
    }
  }, [selectedDuration]);

  const handleDurationSelect = (duration: string) => {
    setShowCustom(false);
    onDurationChange(duration);
  };

  const handleCustomDurationChange = () => {
    if (customDuration && customUnit) {
      const duration = `${customDuration} ${customDuration === '1' ? customUnit : customUnit + 's'}`;
      onDurationChange(duration);
    }
  };

  const formatDuration = (duration: string) => {
    const option = predefinedDurations.find(d => d.value === duration);
    return option ? option.label : duration;
  };

  const getDurationDescription = (duration: string) => {
    const option = predefinedDurations.find(d => d.value === duration);
    return option ? option.description : 'Duração personalizada';
  };

  const isNeverDuration = (duration: string) => {
    return duration === 'never';
  };

  const isRecommended = (duration: string) => {
    const option = predefinedDurations.find(d => d.value === duration);
    return option?.recommended || duration === defaultDuration;
  };

  const isEmergencyDuration = (duration: string) => {
    const option = predefinedDurations.find(d => d.value === duration);
    return option?.emergency;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-700">Duração do Acesso *</h3>
          {onEmergencyChange && (
            <Button
              type="button"
              variant={isEmergency ? "destructive" : "outline"}
              size="sm"
              onClick={() => onEmergencyChange(!isEmergency)}
              className="text-xs"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              {isEmergency ? 'Emergência Ativa' : 'Marcar como Emergência'}
            </Button>
          )}
        </div>
        
        {isEmergency && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">
              <strong>Modo Emergência:</strong> Apenas durações curtas estão disponíveis. 
              O acesso será limitado para garantir a segurança dos dados.
            </AlertDescription>
          </Alert>
        )}

        {patientSettings?.auto_approve_duration && (
          <Alert className="border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              <strong>Configuração do Paciente:</strong> Duração padrão de {formatDuration(patientSettings.auto_approve_duration)} 
              para aprovação automática.
            </AlertDescription>
          </Alert>
        )}

        <p className="text-xs text-gray-500">
          Selecione por quanto tempo você precisa do acesso aos dados
        </p>
      </div>

      {/* Durações Predefinidas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {availableDurations.map((duration) => {
          const isSelected = selectedDuration === duration.value;
          const recommended = isRecommended(duration.value);
          const emergency = isEmergencyDuration(duration.value);
          const isNever = isNeverDuration(duration.value);
          
          return (
            <Card 
              key={duration.value}
              className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                isSelected 
                  ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200' 
                  : 'hover:bg-gray-50'
              } ${
                emergency ? 'border-red-200' : ''
              } ${
                isNever ? 'border-purple-200 bg-purple-50' : ''
              }`}
              onClick={() => handleDurationSelect(duration.value)}
            >
              <CardContent className="p-3">
                <div className="text-center space-y-2">
                  <div className="flex items-center justify-center">
                    {isNever ? (
                      <Infinity className={`h-4 w-4 ${
                        isSelected ? 'text-purple-600' : 'text-purple-500'
                      }`} />
                    ) : (
                      <Clock className={`h-4 w-4 ${
                        isSelected ? 'text-blue-600' : 'text-gray-500'
                      }`} />
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {duration.label}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {duration.description}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {recommended && (
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        Recomendado
                      </Badge>
                    )}
                    {emergency && (
                      <Badge className="bg-red-100 text-red-800 text-xs">
                        Emergência
                      </Badge>
                    )}
                    {isNever && (
                      <Badge className="bg-purple-100 text-purple-800 text-xs">
                        Permanente
                      </Badge>
                    )}
                    {isSelected && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        Selecionado
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Duração Personalizada */}
      {!isEmergency && (
        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowCustom(!showCustom)}
            className="w-full"
          >
            <Calendar className="h-4 w-4 mr-2" />
            {showCustom ? 'Ocultar Duração Personalizada' : 'Definir Duração Personalizada'}
          </Button>

          {showCustom && (
            <Card className="border-dashed">
              <CardContent className="p-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Duração Personalizada</h4>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={customDuration}
                      onChange={(e) => setCustomDuration(e.target.value)}
                      placeholder="Quantidade"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <select
                      value={customUnit}
                      onChange={(e) => setCustomUnit(e.target.value as any)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="hours">Horas</option>
                      <option value="days">Dias</option>
                      <option value="weeks">Semanas</option>
                      <option value="months">Meses</option>
                    </select>
                    <Button
                      type="button"
                      onClick={handleCustomDurationChange}
                      disabled={!customDuration}
                      size="sm"
                    >
                      Aplicar
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Defina uma duração específica para suas necessidades
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Resumo da Duração Selecionada */}
      {selectedDuration && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-green-900">
                  Duração Selecionada: {formatDuration(selectedDuration)}
                </h4>
                <p className="text-xs text-green-700">
                  {getDurationDescription(selectedDuration)}
                </p>
              </div>
              <Clock className="h-4 w-4 text-green-600" />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DurationSelector;